/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo } from 'react';

import { useParams } from 'js/lib/useRouter';

import { Grid, Link, PageGridContainer, Typography, Typography2 } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import { create, isEmpty } from 'bundles/cml';
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import CollectionDescription from 'bundles/enterprise-collections/components/CollectionDescription';
import EnterpriseLearningPathsNotConfigurable from 'bundles/page-config-common/components/enterprise/EnterpriseLearningPathsNotConfigurable';
import CollectionModule from 'bundles/page-config-common/components/shared/CollectionModule';
import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import { useComponentsConfig } from 'bundles/page-config-common/contexts/ComponentsConfigContext';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import { useAcademicDisciplineName } from 'bundles/page-config-common/contexts/enterprise/AcademicDisciplineContext';
import { useEnterpriseBadgeCollections } from 'bundles/page-config-common/contexts/enterprise/EnterpriseBadgeCollectionsContext';
import { useEnterpriseCollections } from 'bundles/page-config-common/contexts/enterprise/EnterpriseCollectionsContext';
import { useCuratedCollections } from 'bundles/page-config-common/contexts/enterprise/EnterpriseCuratedCollectionsContext';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  CURATED_COLLECTION_MAX_LIMIT,
  CURATED_COLLECTION_MIN_LIMIT,
  ENTERPRISE_COLLECTION_TYPES,
} from 'bundles/unified-home-common/constants';

import _t from 'i18n!nls/consumer-home';

const styles = {
  enterpriseCollectionHeader: css`
    margin-top: var(--cds-spacing-150);
  `,
  header: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  button: css`
    padding-left: 0;
    padding-right: 0;
  `,
};

const MAX_COLLECTION_ENTITY_LIMIT = 24;

export const filterCuratedCollections = (
  enterpriseCollections: CarouselCollection[] | undefined,
  titleComponent: 'h3' | 'h4'
) => {
  const curatedCollections = enterpriseCollections
    ?.filter((collection) => collection?.collectionType === ENTERPRISE_COLLECTION_TYPES.CURATED)
    .filter((collection) => collection?.entities?.length > 0);

  return curatedCollections?.map((collection) => ({
    ...collection,
    entities: collection.entities.map((entity) => ({
      ...entity,
      titleComponent,
    })),
  }));
};

const EnterpriseCuratedCollectionAndBadgeModuleRenderer = () => {
  const { programSlug } = useParams();
  const features = useEnterpriseProductFeatures();
  const { isEnterpriseCollectionsLoading, enterpriseCollections } = useEnterpriseCollections();
  const { enterpriseBadgesWithCollection, enterpriseBadgeCollectionsLoading } = useEnterpriseBadgeCollections();
  const { totalCuratedCollections } = useCuratedCollections();
  const { academicDisciplineName } = useAcademicDisciplineName();
  const pageConfigComponents = useComponentsConfig();
  const { currentLevel, nextLevel } = useHeaderLevel();

  // We want to show more collections here if the program has disabled browse collections.
  // pageConfigComponents will be loaded for this to render so no need to wait.
  const collectionsToShow =
    pageConfigComponents &&
    pageConfigComponents.filter(
      (component) => component?.__typename === 'PageConfig_EnterpriseBrowseCollectionComponent'
    ).length > 0
      ? CURATED_COLLECTION_MIN_LIMIT
      : CURATED_COLLECTION_MAX_LIMIT;

  const curatedCollections = useMemo(
    () => filterCuratedCollections(enterpriseCollections, nextLevel as 'h3' | 'h4'),
    [enterpriseCollections, nextLevel]
  );
  // We cap the amount of entities in a collection at MAX_COLLECTION_ENTITY_LIMIT. If we hit that limit in a collection,
  // we want to display the link to the view all collections page.
  const collectionMaxLimitHit = useMemo(() => {
    if (curatedCollections?.length) {
      let hitMaxLimitInAnyCollection = false;

      for (let i = 0; i < curatedCollections.length; i += 1) {
        if (curatedCollections[i].entities.length >= MAX_COLLECTION_ENTITY_LIMIT) {
          hitMaxLimitInAnyCollection = true;
          break;
        }
      }

      return hitMaxLimitInAnyCollection;
    } else {
      return false;
    }
  }, [curatedCollections]);

  if (
    !isEnterpriseCollectionsLoading &&
    !curatedCollections?.length &&
    !enterpriseBadgeCollectionsLoading &&
    !enterpriseBadgesWithCollection.length
  ) {
    return null;
  }

  const hasMoreCollectionsThanDisplayed = totalCuratedCollections && totalCuratedCollections > collectionsToShow;
  const showViewAllButton = hasMoreCollectionsThanDisplayed || collectionMaxLimitHit;

  const getFooterViewAllLink = () => {
    // Display this text as an override if there is a limit hit in a collection, but we are showing all available collections.
    if (collectionMaxLimitHit && !hasMoreCollectionsThanDisplayed) {
      return _t('View all recommendations from your organization');
    }

    if (academicDisciplineName) {
      return _t('View all subjects in #{academicDisciplineName}', { academicDisciplineName });
    } else if (features?.programRecommendationsAvailableCoursesCopy) {
      return _t('View all courses');
    } else {
      return _t('View all collections curated by your organization');
    }
  };

  return (
    <HomeSection
      className="PageConfig_EnterpriseCuratedCollectionAndBadgeComponent"
      dataTestId="enterprise-curated-collections"
    >
      <EnterpriseLearningPathsNotConfigurable />
      {isEnterpriseCollectionsLoading ? (
        <CollectionModule isCollectionLoading={isEnterpriseCollectionsLoading} />
      ) : (
        curatedCollections?.slice(0, collectionsToShow).map((curatedCollection) => (
          <CollectionModule
            key={curatedCollection.collectionId}
            isCollectionLoading={isEnterpriseCollectionsLoading}
            collection={curatedCollection}
            customHeader={
              <div css={styles.enterpriseCollectionHeader}>
                <Typography2 variant="titleSmall" component={currentLevel}>
                  {curatedCollection.title}
                </Typography2>
                {curatedCollection.description?.cml &&
                  !isEmpty(
                    create(curatedCollection.description.cml.value, curatedCollection.description.cml.dtdId)
                  ) && (
                    <CollectionDescription
                      collection={{
                        description: curatedCollection.description,
                      }}
                    />
                  )}
              </div>
            }
          />
        ))
      )}
      {!!showViewAllButton && (
        <PageGridContainer>
          <Grid item xs={12}>
            <Typography component="p" variant="h2semibold">
              {_t('Want to see more? ')}
              <Link
                component={TrackedLink2}
                className="collection-footer-link"
                variant="quiet"
                data={{ programSlug }}
                trackingName="curated_collections_footer_view_all_arrow_link"
                href={`/programs/${programSlug}/view-all-collections/curated`}
              >
                {getFooterViewAllLink()} <ArrowNextIcon size="medium" style={{ verticalAlign: 'middle' }} />
              </Link>
            </Typography>
          </Grid>
        </PageGridContainer>
      )}
    </HomeSection>
  );
};

export default EnterpriseCuratedCollectionAndBadgeModuleRenderer;
